import { useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { completeGoogleRedirect, detectLostSignIn, lastLostProvider, signInWithProvider } from '../services/authService';
import { LOST_CODE } from '../services/signInTrouble';
import { postSignInDestination } from '../utils/postSignIn';
import useGoogleSignIn from '../hooks/useGoogleSignIn';
import useSignInNotice from '../hooks/useSignInNotice';
import { emailSignInAvailable, loadEmailSignInAvailability } from '../services/emailLinkSignIn';
import { offerEmailAfterBack, openAccountExistsSheet } from '../services/signInSheet';

// In-page sign-in flows: the user signed in FROM a page that continues its
// own work once auth lands (the claim page redeems its ?token via an effect;
// the feedback page holds a grant to award on submit). Navigating to the
// default destination would yank them off mid-flow — on mobile, where
// sign-in is a full-page redirect, that made emailed links dead ends (found
// 2026-07-27, twice).
const IN_PAGE_FLOWS = ['/claim-credits', '/feedback'];
const AVAILABILITY_WAIT_MS = 2500;

// Completes a redirect-based Google sign-in when the browser returns from
// Google. On every normal load getRedirectResult() resolves to null, so this
// is a cheap no-op. When a user does come back from a redirect it fires the
// sign-in analytics (inside completeGoogleRedirect) and navigates to the same
// post-sign-in destination the popup flow (useGoogleSignIn) uses — so mobile
// and desktop land in the same place.
//
// When the PREVIOUS page life started a sign-in that never finished (the iOS
// popup tab took the page down; a redirect came back empty), it says so and
// offers the retry (#2457) — before, those students saw the landing page
// again with no word about what happened.
//
// Mounted once at app root, inside the Router (to navigate) and the
// ToastProvider (to speak).
export default function RedirectSignInHandler() {
  const navigate = useNavigate();
  const notify = useSignInNotice();
  const { signIn, signInProvider } = useGoogleSignIn();
  const signInRef = useRef(signIn);
  signInRef.current = signIn;
  // The provider the redirect went to (#5154: Google, Apple or Facebook).
  const providerRef = useRef(signInProvider);
  providerRef.current = signInProvider;
  // A Google sign-in that did not finish (#3894 Back, #4117 a return by
  // another page or a lost one): with the email link on, the email sheet,
  // every time, with "Try Google again" on it (#3928). The answer on
  // availability may still be in flight on a fresh load, so it is awaited.
  // Returns whether the sheet opened.
  const offerRef = useRef(null);
  offerRef.current = async (provider = 'google') => {
    if (IN_PAGE_FLOWS.includes(window.location.pathname)) return false;
    // A slow answer must not leave a lost sign-in unannounced (reviewer,
    // #4117): past the wait, the notice speaks instead.
    await Promise.race([loadEmailSignInAvailability(), new Promise((r) => { setTimeout(r, AVAILABILITY_WAIT_MS); })]);
    if (!emailSignInAvailable()) return false;
    return offerEmailAfterBack({ entry: 'redirect_retry', retry: provider,
      onProvider: (p, pos) => providerRef.current(p, 'redirect_retry', pos),
      onGoogle: () => providerRef.current('google', 'redirect_retry') });
  };
  const onBack = useCallback((provider) => { offerRef.current?.(provider); }, []);
  // Account exists, met on the way back from the provider's page (#5154).
  const onAccountExists = useCallback((info) => {
    openAccountExistsSheet({ entry: 'redirect_retry', info,
      onProvider: (p, pos) => providerRef.current(p, 'redirect_retry', pos) });
  }, []);
  const notifyRef = useRef(notify);
  notifyRef.current = notify;
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;

  // Once per mount, which is once per page load: this sits above <Routes>
  // and never remounts. NOT keyed on `navigate`: react-router hands out a
  // new navigate on every route change, so the effect re-ran on each one and
  // read a popup still open in this page as lost (#3483, a Mac student told
  // "didn't finish" while Google's window was up).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const user = await completeGoogleRedirect({ onAccountExists });
      if (!user?.email) {
        // Not gated on `cancelled`: the marker is consumed once, so a
        // StrictMode remount would otherwise swallow the only notice.
        const reason = await detectLostSignIn({ onBack });
        if (reason) {
          // The email way in first (#4117): the founder's call, after iOS
          // students retried Google into the same page up to 4 times. The
          // notice and its retry stay for when the email link is off
          // and on the claim/feedback pages.
          const provider = lastLostProvider();
          if (await offerRef.current?.(provider)) return;
          const inPage = IN_PAGE_FLOWS.includes(window.location.pathname);
          const retry = async () => {
            if (!inPage) { signInRef.current('redirect_retry'); return; }
            try { await signInWithProvider(provider, { entry: 'redirect_retry' }); } catch (e) { notifyRef.current(e, retry); }
          };
          notifyRef.current({ code: LOST_CODE, reason, provider }, retry);
        }
        return;
      }
      if (cancelled) return;
      if (IN_PAGE_FLOWS.includes(window.location.pathname)) return;
      try {
        navigateRef.current(await postSignInDestination(user.email));
      } catch {
        // Destination lookup failed (e.g. cold backend) — auth still succeeded,
        // onAuthStateChanged has the user; leave them on the current page.
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // A page iOS Safari restores from the back/forward cache runs none of the
  // above: no load, no mount (#3894). A redirect marker this page life set
  // then means the student went to Google and came Back without choosing an
  // account; detectLostSignIn counts it (cancelled) and says nothing, so the
  // start no longer reads as a silent loss on the Desk.
  useEffect(() => {
    const onShow = (e) => { if (e.persisted) detectLostSignIn({ restored: true, onBack }); };
    window.addEventListener('pageshow', onShow);
    return () => window.removeEventListener('pageshow', onShow);
  }, []);

  return null;
}
